// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * Dependency-free SVG chart primitives (D-SHIP-REPORT-1, Lucas TL + Maya PM
 * sign-off 2026-07-17). The app has no charting library; adding one to the
 * production bundle for a single screen isn't justified. These are pure,
 * presentational components — data in, SVG out — reusable across report
 * surfaces. Axes, gridlines, value labels, and hover tooltips included so the
 * output reads as a real report, not a stack of bare divs.
 *
 * All colors come from theme tokens with hard fallbacks so charts render
 * correctly in both themes and in print.
 */
import { useState } from 'react'

export interface BarDatum { label: string; value: number; sublabel?: string }

const AXIS = 'var(--color-text-muted, #64748b)'
const GRID = 'var(--color-border, #e2e8f0)'
const INK = 'var(--color-text, #0f172a)'

function niceMax(v: number): number {
  if (v <= 0) return 1
  const mag = Math.pow(10, Math.floor(Math.log10(v)))
  const n = v / mag
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
  return step * mag
}

/** Vertical bar chart with y-axis, gridlines, value labels and hover tooltip. */
export function BarChart({
  data, height = 220, color = 'var(--color-primary, #0d9488)',
  valueFormat = (v: number) => String(v), yTicks = 4, maxBarLabels = 31,
}: {
  data: BarDatum[]
  height?: number
  color?: string
  valueFormat?: (v: number) => string
  yTicks?: number
  maxBarLabels?: number
}) {
  const [hover, setHover] = useState<number | null>(null)
  const padL = 52, padR = 12, padT = 12, padB = 30
  const W = 720, innerW = W - padL - padR, innerH = height - padT - padB
  const rawMax = Math.max(0, ...data.map(d => d.value))
  const max = niceMax(rawMax)
  const n = data.length || 1
  const gap = n > 40 ? 1 : 3
  const bw = Math.max(1, (innerW - gap * (n - 1)) / n)
  const x = (i: number) => padL + i * (bw + gap)
  const y = (v: number) => padT + innerH - (v / max) * innerH
  const showLabels = n <= maxBarLabels

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} role="img"
           preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
        {/* y gridlines + ticks */}
        {Array.from({ length: yTicks + 1 }, (_, i) => {
          const v = (max / yTicks) * i
          const yy = y(v)
          return (
            <g key={i}>
              <line x1={padL} x2={W - padR} y1={yy} y2={yy} stroke={GRID} strokeWidth={1} />
              <text x={padL - 6} y={yy + 3} textAnchor="end" fontSize={10} fill={AXIS}>{valueFormat(v)}</text>
            </g>
          )
        })}
        {/* bars */}
        {data.map((d, i) => {
          const bh = d.value > 0 ? Math.max(2, (d.value / max) * innerH) : 0
          const active = hover === i
          return (
            <g key={i}
               onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              {/* invisible full-height hit area for easy hover */}
              <rect x={x(i)} y={padT} width={bw} height={innerH} fill="transparent" />
              <rect x={x(i)} y={y(d.value)} width={bw} height={bh}
                    fill={color} rx={Math.min(2, bw / 3)}
                    opacity={hover === null || active ? 1 : 0.55} />
            </g>
          )
        })}
        {/* x labels (only when they fit) */}
        {showLabels && data.map((d, i) => (
          <text key={i} x={x(i) + bw / 2} y={height - padB + 14} textAnchor="middle"
                fontSize={10} fill={AXIS}>{d.label}</text>
        ))}
        {/* baseline */}
        <line x1={padL} x2={W - padR} y1={padT + innerH} y2={padT + innerH} stroke={AXIS} strokeWidth={1} />
      </svg>
      {hover !== null && data[hover] && (
        <div style={{
          position: 'absolute', left: `${((x(hover) + bw / 2) / W) * 100}%`, top: 0,
          transform: 'translate(-50%, -100%)', pointerEvents: 'none',
          background: INK, color: '#fff', padding: '5px 9px', borderRadius: 6,
          fontSize: 12, whiteSpace: 'nowrap', zIndex: 2, boxShadow: '0 4px 12px rgba(0,0,0,.18)',
        }}>
          <div style={{ fontWeight: 600 }}>{data[hover].sublabel || data[hover].label}</div>
          <div>{valueFormat(data[hover].value)}</div>
        </div>
      )}
    </div>
  )
}

/** Donut chart with center total and a legend. */
export function DonutChart({
  data, size = 160, thickness = 26, valueFormat = (v: number) => String(v), centerLabel,
}: {
  data: { label: string; value: number; color: string }[]
  size?: number
  thickness?: number
  valueFormat?: (v: number) => string
  centerLabel?: string
}) {
  const [hover, setHover] = useState<number | null>(null)
  const total = data.reduce((a, d) => a + d.value, 0)
  const r = (size - thickness) / 2
  const cx = size / 2, cy = size / 2
  const circ = 2 * Math.PI * r
  let offset = 0
  const segs = data.map((d) => {
    const frac = total > 0 ? d.value / total : 0
    const seg = { ...d, frac, dash: frac * circ, off: offset }
    offset += frac * circ
    return seg
  })

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={GRID} strokeWidth={thickness} opacity={0.35} />
        {segs.map((s, i) => (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
                  stroke={s.color} strokeWidth={hover === i ? thickness + 4 : thickness}
                  strokeDasharray={`${s.dash} ${circ - s.dash}`}
                  strokeDashoffset={-s.off}
                  transform={`rotate(-90 ${cx} ${cy})`}
                  opacity={hover === null || hover === i ? 1 : 0.5}
                  style={{ transition: 'stroke-width .12s' }}
                  onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
        ))}
        <text x={cx} y={cy - 2} textAnchor="middle" fontSize={20} fontWeight={700} fill={INK}>
          {valueFormat(total)}
        </text>
        {centerLabel && (
          <text x={cx} y={cy + 15} textAnchor="middle" fontSize={10} fill={AXIS}>{centerLabel}</text>
        )}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 140 }}>
        {segs.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5,
                 opacity: hover === null || hover === i ? 1 : 0.6, cursor: 'default' }}
               onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{s.label}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', color: AXIS }}>
              {valueFormat(s.value)}{total > 0 ? ` · ${Math.round(s.frac * 100)}%` : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
